/* ============================================================
   Dathent — Content Plan
   Strategy → Pillars → Calendar single-flow per design pack.
   Source: pages/content-plan.jsx + Content Plan.html.
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons } from '../components/pack-icons/brands';
import { useActivity } from '../components/AppShell';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { useGenerationStatus, OPERATION_KINDS } from '../state/GenerationStatusContext.jsx';
import MarketingSubnav from '../components/MarketingSubnav.jsx';
import NoBrandGuard from '../components/NoBrandGuard.jsx';
import { missingBrandFields, hasRealText } from '../lib/activationProgress.js';
import { creationAccessState } from '../components/billing/CreationAccessNotice.jsx';

// Palette assigned to pillars by index when backend doesn't supply a color.
const PILLAR_COLORS = ['#6E5BFF', '#4FC3FF', '#E455CF', '#F5B544', '#15803D', '#FF8B6B'];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function errMessage(err) {
  if (!err) return 'Unknown error';
  return err?.body?.error?.message || err?.body?.message || err?.message || 'Unknown error';
}

function startOfWeek(d = new Date()) {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const s = new Date(d);
  s.setDate(d.getDate() + diff);
  s.setHours(0, 0, 0, 0);
  return s;
}

function buildWeekRanges(weeksAhead = 4) {
  const monday = startOfWeek();
  const weeks = [];
  for (let i = 0; i < weeksAhead; i++) {
    const start = new Date(monday);
    start.setDate(monday.getDate() + i * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    weeks.push({
      idx: i,
      label: `Week ${i + 1}`,
      range: `${start.toLocaleString('en', { month: 'short' })} ${start.getDate()}–${end.getDate()}`,
      start,
      end,
    });
  }
  return weeks;
}

/* ============================================================
   Strategy block
   ============================================================ */
function StrategyBlock({ strategy, brand, onEdit, onRegenerate, research, busy, justDone, regenExhausted, regenAccessAllowed }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Research grounding lives in the strategy SNAPSHOT (nested under
  // strategy.strategy after the API mapping) — the top-level fallback covers
  // older shapes and mocks.
  const snapshot = strategy?.strategy || {};
  const groundedId = strategy?.researchId ?? snapshot.researchId ?? null;
  const groundedVersion = strategy?.researchVersion ?? snapshot.researchVersion ?? null;
  // A newer READY research exists than the one this plan was built on.
  const researchStale = Boolean(groundedId && research?.ready && research?.latestId && research.latestId !== groundedId);
  const groundedDate = (() => {
    if (!groundedId || !strategy?.updatedAt) return null;
    try {
      return new Date(strategy.updatedAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
    } catch {
      return null;
    }
  })();
  const goal = strategy?.summary || strategy?.goal || brand?.goal || '—';
  // TASK-327: brand.targetAudience carries onboarding's literal placeholder
  // string for a name-only brand ("Target audience not provided") — never
  // render it verbatim as if it were real audience copy.
  const audienceRaw = strategy?.targetAudienceSummary || strategy?.audience || brand?.audience || brand?.targetAudience || '';
  const audience = hasRealText(audienceRaw) ? audienceRaw : t('contentPlan.audienceMissing');
  const positioning = strategy?.positioning || brand?.positioning || '—';
  // Brand identity. Resilient to either field naming convention the
  // API returns (BrandProfile type → `businessName` + `websiteUrl`;
  // some older code paths used `brandName` / `website` so we keep the
  // fallbacks defensive).
  const brandName = brand?.businessName || brand?.brandName || brand?.name || null;
  const brandLink = brand?.websiteUrl || brand?.website || null;
  // Pretty display of the URL — strip scheme + trailing slash so it
  // reads as "p-de-p.com" not "https://p-de-p.com/". Falls back to
  // the raw value if URL parsing fails (e.g. user typed an @handle).
  const brandLinkLabel = (() => {
    if (!brandLink) return null;
    try {
      const u = new URL(brandLink);
      return `${u.host}${u.pathname === '/' ? '' : u.pathname}`.replace(/\/$/, '');
    } catch {
      return brandLink;
    }
  })();
  const cadence = (() => {
    const snap = strategy?.strategy;
    const pillarsCount = Array.isArray(strategy?.contentPillars) ? strategy.contentPillars.length : 0;
    if (snap?.cadence) return snap.cadence;
    return { posts: pillarsCount * 6, weeks: 4, perWeek: pillarsCount * 1.5 };
  })();

  // Brand logo (defensive across naming) → header tile; monogram fallback so the
  // header always shows something, never a broken/empty slot.
  const brandLogo = brand?.logoUrl || brand?.logo || brand?.primaryLogoUrl || null;
  const monogram = (brandName || 'B').trim().charAt(0).toUpperCase();
  const latestVersion = research?.latestVersion ?? null;
  const behind = (groundedVersion != null && latestVersion != null)
    ? Math.max(0, Number(latestVersion) - Number(groundedVersion)) : null;
  const brandGrounded = !groundedId;
  // A plan actually exists to be rebuilt. During FIRST setup there is none yet:
  // research turns ready seconds before the very first strategy commits, and
  // without this check the card claimed "a newer research is ready — rebuild"
  // for a plan that had never been built, and offered a Rebuild button that
  // raced the activation chain into a second paid build (owner-caught
  // 2026-08-06). `busy` alone did not suppress it, because the first build's
  // in-flight signal was invisible to this screen.
  const hasStrategy = Boolean(strategy && (strategy.id || snapshot.summary || strategy.summary));
  // The stale callout (the key interactive moment) shows when a newer/ready
  // research exists that this plan isn't built on. Hidden while regenerating.
  const showStale = !busy && hasStrategy && Boolean(research && (researchStale || (!groundedId && research.ready)));
  // Rebuilding the plan is gated on the content_plan_regenerations_monthly
  // allowance, NOT the research feature: Free gets 1 rebuild/month (catch up to
  // the latest research — cheap, no Tavily), paid 5/15/30. Locked only once that
  // month's allowance is spent (regenExhausted from the parent); then the button
  // routes to billing. (New research itself stays paid — research_runs = 0.)
  const regenLocked = Boolean(regenExhausted);
  // TASK-327: a brand with no real offer/audience yet (placeholder-only, e.g.
  // straight off the posting→AI-plan upgrade) can't ground a plan at all —
  // strategy-worker 400s ("productOrService or offer is required"). Gate
  // BEFORE the quota check so the button never dead-ends into that error; the
  // fix here is completing the brand, not billing.
  const regenBlockedByBrand = missingBrandFields(brand).length > 0;
  const goBilling = () => navigate('/app/settings?tab=billing');
  const regenLabel = (research?.ready || groundedId)
    ? t('contentPlan.regenerateFromResearch') : t('contentPlan.regenerateFromBrand');
  const doneVersion = justDone?.version ?? latestVersion ?? groundedVersion;
  const cadenceUnits = t('contentPlan.cadenceUnits', { weeks: cadence?.weeks ?? 4, perWeek: cadence?.perWeek ?? 0 });

  return (
    <div className="stv">
      <section className={`strat ${busy ? 'locked' : ''}`}>
        {/* brand header */}
        <div className="st-head">
          <div className="glow" />
          {/* Logo tile doubles as the "change your logo" entry — clicks through
              to Media (Brand Kit), where the logo is managed. */}
          {brandLogo
            ? <div className="logo" role="button" tabIndex={0} onClick={() => navigate('/app/brandkit')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/app/brandkit'); }} title={t('contentPlan.changeLogoHint')} style={{ backgroundImage: `url("${brandLogo}")`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: 'transparent', color: 'transparent', cursor: 'pointer' }} aria-label={t('contentPlan.changeLogoHint')}>{monogram}</div>
            : <div className="logo" role="button" tabIndex={0} onClick={() => navigate('/app/brandkit')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/app/brandkit'); }} title={t('contentPlan.changeLogoHint')} style={{ cursor: 'pointer' }} aria-label={t('contentPlan.changeLogoHint')}>{monogram}</div>}
          <div className="bn">
            <div className="nm">{brandName || t('contentPlan.yourBrand')}</div>
            {brandLinkLabel && (
              <a className="url" href={brandLink} target="_blank" rel="noopener noreferrer">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" /></svg>
                {brandLinkLabel}
              </a>
            )}
          </div>
          {brandGrounded ? (
            <span className="prov brand"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" /></svg>{t('contentPlan.builtOnBrand')}</span>
          ) : (
            <button type="button" className={`prov ${justDone ? 'upd' : ''}`} onClick={() => navigate('/app/research')} title={t('contentPlan.openResearch')}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
              {t('contentPlan.builtOnResearch')} {groundedVersion ? <b>v{doneVersion}</b> : null}{groundedDate ? ` · ${groundedDate}` : ''}
            </button>
          )}
        </div>

        {/* title */}
        <div className="st-title">
          <h2>{t('contentPlan.strategyHeadingLead')} {t('contentPlan.strategyHeadingAccent')}</h2>
          <p>{t('contentPlan.strategyIntro')}</p>
        </div>

        {/* regenerating — durable running state (locked card + inline status) */}
        {busy && (
          <div className="regen" role="status" aria-live="polite">
            <div className="top">
              <span className="spin" aria-hidden="true" />
              <span className="tx">
                <span className="t">{latestVersion ? t('contentPlan.regeneratingFromV', { version: `v${latestVersion}` }) : t('contentPlan.rebuildingTitle')}</span>
                <span className="h"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>{t('contentPlan.underMinute')}</span>
              </span>
            </div>
            <div className="bar"><i /></div>
            <div className="leave"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>{t('contentPlan.youCanLeave')}</div>
          </div>
        )}

        {/* stale callout — the key interactive moment, Rebuild inside it */}
        {showStale && (
          <>
            <div className="stale">
              <span className="ic"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 13.5 8 18 9.5 13.5 11 12 16 10.5 11 6 9.5 10.5 8z" /></svg></span>
              <span className="tx">
                <span className="t">{t('contentPlan.staleTitle', { version: latestVersion ? `v${latestVersion}` : '' })}</span>
                <span className="h">{t('contentPlan.staleSub')}</span>
              </span>
              <button type="button" className="btn btn-gold" onClick={regenLocked ? goBilling : onRegenerate} disabled={!regenAccessAllowed || Boolean(research?.active) || regenBlockedByBrand} title={regenBlockedByBrand ? t('contentPlan.regenerateNeedsBrand') : (regenLocked ? t('contentPlan.regenLockedFree') : undefined)}>
                {regenBlockedByBrand || regenLocked
                  ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" /></svg>
                  : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" /></svg>}
                {regenBlockedByBrand ? t('contentPlan.regenerateNeedsBrand') : t('contentPlan.rebuildPlan')}
              </button>
            </div>
            {/* Fix (judge critic, item 13): `.stale` is `display:flex`, so a
                `display:block` Link INSIDE it was still laid out as another
                flex item on the same row (squeezed next to the gold button),
                not as a full-width line underneath it. Render it as a sibling
                line after the row instead. */}
            {regenBlockedByBrand && (
              <p className="stale-hint">
                <Link to="/app/plan/edit-strategy#business" className="eyebrow stv-foot-note">
                  {t('contentPlan.regenerateNeedsBrandHint')}
                </Link>
              </p>
            )}
          </>
        )}

        {/* four lines — the hero */}
        <div className="lines">
          <div className="line">
            <div className="lh"><span className="ic"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="0.5" fill="currentColor" /></svg></span><span className="t">{t('contentPlan.goalLabel')}</span></div>
            <p>{goal}</p>
          </div>
          <div className="line">
            <div className="lh"><span className="ic"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="8" r="4" /><path d="M2 21a7 7 0 0 1 14 0" /><path d="M16 3a4 4 0 0 1 0 8M22 21a7 7 0 0 0-6-7" /></svg></span><span className="t">{t('contentPlan.audienceLabel')}</span></div>
            <p>{audience}</p>
          </div>
          <div className="line">
            <div className="lh"><span className="ic"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 13.5 8 18 9.5 13.5 11 12 16 10.5 11 6 9.5 10.5 8z" /></svg></span><span className="t">{t('contentPlan.positioningLabel')}</span></div>
            <p>{positioning}</p>
          </div>
          <div className="line cadence">
            <div className="lh"><span className="ic"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></svg></span><span className="t">{t('contentPlan.cadenceLabel')}</span></div>
            {cadence?.posts > 0 ? (
              <>
                <span className="bars"><i style={{ height: 10 }} /><i style={{ height: 16 }} /><i style={{ height: 12 }} /><i style={{ height: 20 }} /><i style={{ height: 14 }} /></span>
                <span className="metric"><span className="n">{cadence.posts}</span><span className="u">{cadenceUnits}</span></span>
              </>
            ) : (
              <span className="metric"><span className="u">{t('contentPlan.cadencePending')}</span></span>
            )}
          </div>
        </div>

        {/* actions */}
        <div className="st-foot">
          <button type="button" className="btn btn-ghost" onClick={onEdit} disabled={busy || Boolean(research?.active)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
            {t('contentPlan.editStrategy')}
          </button>
          <span className="sp" />
          {busy ? (
            <span className="eyebrow stv-foot-note">{t('contentPlan.lockedRebuilding')}</span>
          ) : (researchStale && groundedId) ? (
            <span className="eyebrow stv-foot-note">{t('contentPlan.planBehind', { version: `v${groundedVersion}`, behind })}</span>
          ) : !showStale ? (
            <button type="button" className="btn btn-ghost" onClick={regenLocked ? goBilling : onRegenerate} disabled={!regenAccessAllowed || Boolean(research?.active) || regenBlockedByBrand} title={regenBlockedByBrand ? t('contentPlan.regenerateNeedsBrand') : (regenLocked ? t('contentPlan.regenLockedFree') : undefined)}>
              {regenBlockedByBrand || regenLocked
                ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" /></svg>
                : regenLabel === t('contentPlan.regenerateFromBrand')
                ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" /></svg>
                : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" /></svg>}
              {regenBlockedByBrand ? t('contentPlan.regenerateNeedsBrand') : regenLabel}
            </button>
          ) : null}
          {research?.active && (
            <span className="eyebrow stv-foot-note"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg> {t('contentPlanScreen.researchRunningBanner')}</span>
          )}
        </div>
        {/* Fix (judge critic, item 13, extended to this sibling branch): same
            bug as the `.stale` callout above — `.st-foot` is ALSO
            `display:flex`, so this hint rendered squeezed next to the "Edit
            strategy"/regen buttons instead of as a line underneath them. */}
        {!busy && !(researchStale && groundedId) && !showStale && regenBlockedByBrand && (
          <p className="stale-hint">
            <Link to="/app/plan/edit-strategy#business" className="eyebrow stv-foot-note">
              {t('contentPlan.regenerateNeedsBrandHint')}
            </Link>
          </p>
        )}
      </section>

      {/* persistent activity chip — the durable-job signal (bottom-right) */}
      {busy && (
        <div className="actbar"><div className="actchip"><span className="dot" aria-hidden="true" /><span className="l">{t('contentPlan.actRebuilding')} <b>· {latestVersion ? `research v${latestVersion}` : 'research'}</b></span></div></div>
      )}

      {/* completion — loud, unmistakable finish */}
      {justDone && (
        <>
          <div className="toast" role="status">
            <span className="ic"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="m5 12 5 5 9-11" /></svg></span>
            <span className="tx"><b>{t('contentPlan.toastRebuilt')}</b> <span className="v">— {t('contentPlan.toastNowOn', { version: doneVersion ? `v${doneVersion}` : '' })}</span></span>
          </div>
          <div className="actbar"><div className="actchip done"><span className="dot"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m5 12 5 5 9-11" /></svg></span><span className="l">{t('contentPlan.toastRebuilt')} <b>· {doneVersion ? `research v${doneVersion}` : 'research'}</b></span></div></div>
        </>
      )}
    </div>
  );
}

/* ============================================================
   Pillars block
   ============================================================ */
function PillarsBlock({ pillars, active, setActive }) {
  const { t } = useTranslation();
  if (!pillars?.length) {
    return (
      <section className="cps-pillars">
        <div className="cps-block-h">
          <div className="eyebrow"><span>{t('contentPlan.pillarsEyebrow')}</span></div>
          <h2>{t('contentPlan.noPillarsTitle')}</h2>
          <p>{t('contentPlan.noPillarsBody')}</p>
        </div>
      </section>
    );
  }
  // Equal share for now if backend doesn't supply weights
  const totalShare = pillars.reduce((s, p) => s + (p.share || 100 / pillars.length), 0);
  // Hide the marketing-y subhead when pillars are sparse / weak — it
  // reads as filler when "1 theme, balanced" stares back at the user.
  const showSubhead = pillars.length >= 3;
  return (
    <section className="cps-pillars">
      <div className="cps-block-h">
        <div className="eyebrow"><span>{t('contentPlan.pillarsEyebrow')}</span></div>
        <h2>
          {t('contentPlan.themeCount', { count: pillars.length })}
          {showSubhead ? <>, <span className="cps-h-ac">{t('contentPlan.themeBalancedAccent')}</span></> : '.'}
        </h2>
        {showSubhead && (
          <p>{t('contentPlan.pillarsRotateHint')}</p>
        )}
      </div>

      <div className="cps-vol">
        {pillars.map((p, i) => {
          const share = p.share || (100 / pillars.length);
          const color = p.color || PILLAR_COLORS[i % PILLAR_COLORS.length];
          return (
            <div
              key={p.id || p.name}
              className={`cps-vol-seg ${active === (p.id || p.name) ? 'is-active' : ''}`}
              style={{ flex: share, background: color }}
              onClick={() => setActive(active === (p.id || p.name) ? null : (p.id || p.name))}
              title={`${p.name} — ${Math.round(share)}%`}
            >
              <span className="cps-vol-label">{Math.round(share)}%</span>
            </div>
          );
        })}
      </div>

      <div className="cps-pillar-list">
        {pillars.map((p, i) => {
          const color = p.color || PILLAR_COLORS[i % PILLAR_COLORS.length];
          const id = p.id || p.name;
          return (
            <button
              key={id}
              className={`cps-pillar ${active === id ? 'is-active' : ''}`}
              onClick={() => setActive(active === id ? null : id)}
              type="button"
            >
              <span className="cps-pillar-dot" style={{ background: color }} />
              <div className="cps-pillar-mid">
                <div className="cps-pillar-name">{p.name}</div>
                <div className="cps-pillar-meta">
                  {p.share && <><span className="mono">{Math.round(p.share)}%</span><span className="cps-pillar-sep">·</span></>}
                  <span>{p.tone || p.purpose || t('contentPlan.themeFallback')}</span>
                </div>
              </div>
              <span className="cps-pillar-arr"><I.chevDown size={13} /></span>
            </button>
          );
        })}
      </div>

      {active && (() => {
        const p = pillars.find((x) => (x.id || x.name) === active);
        if (!p) return null;
        const i = pillars.indexOf(p);
        const color = p.color || PILLAR_COLORS[i % PILLAR_COLORS.length];
        const pieces = p.pieces || p.exampleTopics || [];
        return (
          <div className="cps-pillar-detail">
            <div className="cps-pd-h">
              <span className="cps-pillar-dot" style={{ background: color, width: 14, height: 14 }} />
              <h4>{p.name}</h4>
              {p.share && (
                <span
                  className="pill"
                  style={{
                    borderColor: 'transparent',
                    background: `color-mix(in srgb, ${color} 14%, var(--bg-elev))`,
                    color: `color-mix(in srgb, ${color} 80%, var(--text))`,
                  }}
                >
                  {t('contentPlan.percentOfPlan', { percent: Math.round(p.share) })}
                </span>
              )}
            </div>
            <div className="cps-pd-body">
              {pieces.length > 0 && (
                <div>
                  <span className="cps-strat-l">{t('contentPlan.topicIdeas')}</span>
                  <ul className="cps-pd-list">
                    {pieces.map((piece, idx) => (
                      <li key={idx}><I.dot size={10} /> {piece}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(p.example || p.purpose) && (
                <div>
                  <span className="cps-strat-l">{t('contentPlan.whyThisPillar')}</span>
                  <p
                    className="cps-pd-example"
                    style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}
                  >
                    "{p.example || p.purpose}"
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </section>
  );
}

/* ============================================================
   Calendar block (weekly plan from scheduler)
   ============================================================ */
function CalendarBlock({ pillars, active }) {
  const { t } = useTranslation();
  const weeks = useMemo(() => buildWeekRanges(4), []);
  const [weekIdx, setWeekIdx] = useState(0);
  const [calendarItems, setCalendarItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const week = weeks[weekIdx];
    if (!week) return;
    api.scheduler.getCalendar(week.start.toISOString(), week.end.toISOString())
      .then((res) => {
        if (cancelled) return;
        setCalendarItems(res?.data?.posts || res?.posts || []);
      })
      .catch(() => { if (!cancelled) setCalendarItems([]); });
    return () => { cancelled = true; };
  }, [weekIdx, weeks]);

  // Group calendar items by day
  const slotsByDay = useMemo(() => {
    const byDay = {};
    for (const item of calendarItems) {
      const post = item?.post || item;
      const when = post?.scheduledAt || post?.scheduled_at;
      if (!when) continue;
      const d = new Date(when);
      if (Number.isNaN(d.getTime())) continue;
      const dayIdx = (d.getDay() + 6) % 7; // Monday = 0
      const dayLabel = DAYS[dayIdx];
      if (!byDay[dayLabel]) byDay[dayLabel] = [];
      const targets = item?.targets || [];
      const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      for (const t of (targets.length ? targets : [{}])) {
        byDay[dayLabel].push({
          platform: String(t.platform || post.platform || '').toLowerCase(),
          time,
          pillarName: null, // Backend doesn't expose pillar binding yet
        });
      }
    }
    return byDay;
  }, [calendarItems]);

  return (
    <section className="cps-cal">
      <div className="cps-block-h">
        <div className="eyebrow"><span>{t('contentPlan.calendarEyebrow')}</span></div>
        <h2>{t('contentPlan.calendarHeadingLead')} <span className="cps-h-ac">{t('contentPlan.calendarHeadingAccent')}</span></h2>
        <p>{t('contentPlan.calendarIntro')}</p>
      </div>

      <div className="cps-week-bar">
        {weeks.map((w) => {
          const count = w === weeks[weekIdx]
            ? Object.values(slotsByDay).reduce((s, arr) => s + arr.length, 0)
            : null;
          return (
            <button
              key={w.idx}
              className={`cps-week-btn ${weekIdx === w.idx ? 'is-active' : ''}`}
              onClick={() => setWeekIdx(w.idx)}
              type="button"
            >
              <span className="cps-week-l">{w.label}</span>
              <span className="cps-week-r mono">{w.range}</span>
              {count != null && <span className="cps-week-n mono">{t('contentPlan.postsCount', { count })}</span>}
            </button>
          );
        })}
      </div>

      <div className="cps-grid">
        {DAYS.map((day) => {
          const slots = slotsByDay[day] || [];
          return (
            <div key={day} className="cps-day">
              <div className="cps-day-h">
                <span className="cps-day-l">{day}</span>
                <span className="cps-day-c mono">{slots.length}</span>
              </div>
              <div className="cps-day-slots">
                {slots.length === 0 && <div className="cps-day-empty">—</div>}
                {slots.map((s, si) => {
                  // Match pillar by name when backend ever binds them
                  const pillar = pillars?.find((p) => p.name === s.pillarName);
                  const color = pillar?.color
                    || (pillar ? PILLAR_COLORS[pillars.indexOf(pillar) % PILLAR_COLORS.length] : 'var(--accent)');
                  const dim = active && pillar && (pillar.id || pillar.name) !== active;
                  return (
                    <div
                      key={si}
                      className={`cps-slot ${dim ? 'is-dim' : ''}`}
                      style={{ borderLeftColor: color }}
                      title={`${s.platform || ''} · ${s.time}`}
                    >
                      <div className="cps-slot-h">
                        <span className="cps-slot-time mono">{s.time}</span>
                        {s.platform && (
                          <span className={`cps-slot-plat ob-net-ic-${s.platform}`}>
                            {BrandIcons[s.platform]?.({ size: 10 })}
                          </span>
                        )}
                      </div>
                      <div className="cps-slot-pillar">{s.pillarName || s.platform || t('contentPlan.scheduledFallback')}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {pillars?.length > 0 && (
        <div className="cps-cal-foot">
          <div className="cps-cal-legend">
            <span className="cps-legend-l">{t('contentPlan.pillarsEyebrow')}</span>
            {pillars.slice(0, 6).map((p, i) => (
              <span key={p.id || p.name} className="cps-legend-i">
                <i style={{ background: p.color || PILLAR_COLORS[i % PILLAR_COLORS.length] }} />
                {p.name}
              </span>
            ))}
          </div>
          <div className="cps-cal-actions">
            <Link className="btn btn-primary btn-sm" to="/app/create">
              <I.sparkles size={12} /> {t('contentPlan.generatePosts')}
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}

/* ============================================================
   Top-level screen
   ============================================================ */
export const ContentPlan = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setActivity } = useActivity();
  // `active` pillar selection — previously toggled by the PillarsBlock UI
  // (now removed) to highlight a single pillar across the Calendar
  // slots. Without the picker block there's no way to set it, so we hard
  // code null. CalendarBlock still uses pillars[] for slot color coding;
  // it just won't dim anything since `active` is always null.
  const active = null;

  const [strategy, setStrategy] = useState(null);
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  // Research state for this screen: the cross-tab "run in progress" banner
  // plus the strategy provenance strip (flags-gated, polls while active).
  // null while flags are off — the provenance strip stays hidden entirely.
  const [researchState, setResearchState] = useState(null);
  const dash = useDashboardData?.() || {};
  const noBrand = dash.setupStatus?.brand === 'missing';
  useEffect(() => {
    let researchFlags = false;
    try {
      researchFlags = window.localStorage.getItem('dathent.research.enabled') !== '0'
        || window.localStorage.getItem('dathent.research.onboarding') === '1';
    } catch (_) { /* private mode */ }
    if (!researchFlags) return undefined;
    let cancelled = false;
    let timer = null;
    const ACTIVE = ['queued', 'gathering', 'analyzing', 'synthesizing', 'verifying'];
    const probe = async () => {
      try {
        const res = await api.research.list();
        const latest = ((res?.data ?? res)?.reports || [])[0] || null;
        if (cancelled) return;
        const isActive = Boolean(latest && ACTIVE.includes(latest.status));
        setResearchState({
          active: isActive,
          ready: latest?.status === 'ready',
          latestId: latest?.id || null,
          latestVersion: latest?.version || null,
        });
        if (isActive) {
          api.invalidateCache?.('/api/research');
          timer = setTimeout(probe, 20000);
        }
      } catch (_) {
        if (!cancelled) setResearchState({ active: false, ready: false });
      }
    };
    probe();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);
  const researchRunning = researchState?.active;
  // The dashboard data provider loads lazily — without this nudge the
  // entitlements stay null on the plan page and the upsell never renders.
  const ensureDash = dash.ensureDashboardData;
  useEffect(() => { ensureDash?.(); }, [ensureDash]);
  // Rebuild allowance: gate the Rebuild/Regenerate button on the actual
  // content_plan_regenerations_monthly quota (Free 1/mo, paid 5/15/30; null =
  // unlimited = never locked). Free can rebuild once a month to catch the plan
  // up to the latest research; after that the button routes to billing.
  const regenUsage = dash.entitlements?.usage?.content_plan_regenerations_monthly || null;
  const regenQuotaKnown = Boolean(regenUsage && (regenUsage.limit === null
    || (Number.isFinite(regenUsage.limit) && regenUsage.limit >= 0 && Number.isFinite(regenUsage.remaining))));
  const regenAccessAllowed = creationAccessState(dash) === 'allowed' && regenQuotaKnown;
  const regenExhausted = Boolean(regenQuotaKnown && regenUsage.limit !== null && (regenUsage.limit === 0 || regenUsage.remaining < 1));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      api.strategy.get(),
      api.profile.getBrandProfile(),
    ]).then(([sRes, bRes]) => {
      if (cancelled) return;
      if (sRes.status === 'fulfilled') {
        const v = sRes.value;
        setStrategy(v?.data?.strategy || v?.strategy || v?.data || v);
      }
      if (bRes.status === 'fulfilled') {
        const v = bRes.value;
        setBrand(v?.data?.brandProfile || v?.brandProfile || v?.data || v);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const pillars = useMemo(() => {
    // Strategy pillars are rich (name + purpose + exampleTopics) when
    // an AI strategy has been generated. Fall back to brand_profile's
    // raw pillar names (from onboarding) when the strategy is missing
    // pillars — that way the user's 7 typed pillars don't get hidden
    // behind a thin "1 theme" placeholder.
    let list = Array.isArray(strategy?.contentPillars) && strategy.contentPillars.length
      ? strategy.contentPillars
      : (Array.isArray(strategy?.pillars) && strategy.pillars.length ? strategy.pillars : []);
    if (!list.length && Array.isArray(brand?.contentPillars) && brand.contentPillars.length) {
      list = brand.contentPillars.map((name) => ({
        name: String(name),
        // Capitalize-first-letter; backend stores like "skin care education".
        // No purpose/exampleTopics from brand profile — UI handles empty.
        purpose: '',
        exampleTopics: [],
      }));
    }
    return list.map((p, i) => ({
      ...p,
      id: p.id || p.name,
      color: p.color || PILLAR_COLORS[i % PILLAR_COLORS.length],
    }));
  }, [strategy, brand]);

  // While the regeneration request is in flight the whole plan body dims
  // behind a processing overlay — without it the screen sat frozen for
  // 30-60s with no hint anything was happening.
  const [recalculating, setRecalculating] = useState(false); // optimistic bridge until the server op surfaces
  // Transient "just rebuilt" signal → drives the completion toast + provenance
  // pulse + done chip, so the FINISH is unmistakable. Auto-clears after a few s.
  const [justRebuilt, setJustRebuilt] = useState(null); // { version } | null
  // Regeneration is now a DURABLE server async-op (kind plan_regenerate), tracked
  // by the global Activity poll — so progress AND completion survive a reload
  // (the old local-state flow lost everything on refresh).
  const { visibleOperations } = useGenerationStatus();
  const regenOp = useMemo(
    () => (visibleOperations || []).find((o) => o.kind === OPERATION_KINDS.PLAN_REGENERATE) || null,
    [visibleOperations]
  );
  const handledOpRef = useRef(null); // last completed op id we've already reacted to
  const regenBusy = recalculating || regenOp?.status === 'running';

  const handleRecalculate = async () => {
    if (regenBusy || !regenAccessAllowed || regenExhausted || researchRunning || noBrand || missingBrandFields(brand).length > 0) return;
    setRecalculating(true); // bridges the ~1.5s before the op poll surfaces the op
    setJustRebuilt(null);
    try {
      await api.strategy.regenerate(); // 202 + operationId; runs durably in the queue
    } catch (err) {
      setRecalculating(false);
      window.alert(t('contentPlan.recalculateFailed', { error: errMessage(err) }));
      return;
    }
    // Drop the optimistic flag if the op never surfaces; the poll then owns the
    // running state. The generation itself is durable regardless.
    setTimeout(() => setRecalculating(false), 8000);
  };

  // React to the server op finishing: re-fetch the plan + fire the done signal,
  // or surface a failure. Survives reload — on mount the poll re-finds the op,
  // and a completion within the recent-done window still triggers this.
  useEffect(() => {
    if (!regenOp) return;
    if (regenOp.status === 'running') { setRecalculating(false); return; }
    if (handledOpRef.current === regenOp.id) return;
    handledOpRef.current = regenOp.id;
    setRecalculating(false);
    if (regenOp.status === 'succeeded') {
      (async () => {
        // Fresh (cache-busting) read — the op only succeeds AFTER the new plan is
        // persisted, so the stale plan was a caching artifact. Fire the done
        // signal regardless so completion is always visible.
        let newVer = researchState?.latestVersion ?? null;
        try {
          const res = await api.strategy.getFresh();
          const next = res?.data?.strategy || res?.strategy || res?.data || res;
          if (next) setStrategy(next);
          newVer = next?.researchVersion ?? next?.strategy?.researchVersion ?? newVer;
        } catch { /* plan still updated server-side; show done anyway */ }
        setJustRebuilt({ version: newVer });
        setTimeout(() => setJustRebuilt(null), 9000);
      })();
    } else if (regenOp.status === 'failed') {
      // Friendly copy — never the raw provider error. 429 is usually the AI
      // provider's own rate limit (heavy use), which clears on its own.
      const raw = (regenOp.errorMessage || '').toUpperCase();
      window.alert(
        raw.includes('RATE_LIMITED')
          ? t('contentPlan.regenRateLimited')
          : t('contentPlan.recalculateFailed', { error: regenOp.errorMessage || '' })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenOp?.id, regenOp?.status]);

  const handleEdit = () => navigate('/app/plan/edit-strategy');

  return (
    <div className="cps-root">
      <MarketingSubnav />
      {researchRunning && (
        <div className="cp-research-banner" role="status">
          <span className="cp-rb-dot" aria-hidden="true" />
          <span>{t('contentPlanScreen.researchRunningBanner')}</span>
          <button type="button" className="btn btn-soft btn-sm" onClick={() => navigate('/app/research')}>
            {t('contentPlanScreen.researchRunningCta')}
          </button>
        </div>
      )}

      {noBrand ? (
        <div className="cps-plan-body">
          <div>
            <NoBrandGuard surface="plan" />
            <div className="st-foot">
              <button type="button" className="btn btn-ghost" disabled aria-describedby="no-brand-guard-hint-plan">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
                {t('contentPlan.editStrategy')}
              </button>
              <span className="sp" />
              <button type="button" className="btn btn-ghost" disabled aria-describedby="no-brand-guard-hint-plan">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" /></svg>
                {t('contentPlan.regenerateFromBrand')}
              </button>
            </div>
          </div>
          <CalendarBlock pillars={pillars} active={active} />
        </div>
      ) : loading ? (
        <div className="cps-strat">
          <div className="cps-strat-h">
            <h2>{t('contentPlan.loadingStrategy')}</h2>
          </div>
        </div>
      ) : (
        <div className="cps-plan-body">
          <StrategyBlock
            strategy={strategy}
            brand={brand}
            onEdit={handleEdit}
            onRegenerate={handleRecalculate}
            busy={regenBusy}
            justDone={justRebuilt}
            research={researchState}
            regenExhausted={regenExhausted}
            regenAccessAllowed={regenAccessAllowed}
          />
          {/* PillarsBlock removed — pillars still drive AI generation
              (and CalendarBlock slot colors below) but the user-facing
              picker added confusion without clear value. Pillars data
              continues to flow through the same pillars[] derivation. */}
          <CalendarBlock pillars={pillars} active={active} />
          {/* Regenerating state now lives INSIDE the strategy card (locked card +
              inline status + persistent activity chip) — no separate overlay. */}
        </div>
      )}
    </div>
  );
};

export default ContentPlan;
